import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SINGLE_VIEW_MODE } from '../../../../constants';
import { useCurrency, useViewMode } from '../../../../store/app/appStore';
import { useCampaignTypeById } from '../../store/campaignsStore';
import type { CampaignInfoItem, CampaignInfoType, CampaignSource } from '../../types/campaign.types';
import { CAMPAIGN_INFO_TYPES } from '../../types/campaign.types';
import { formatNumberWithSpaces } from '../../utils/campaignHelpers';
import { useCampaignCardLiveData } from '../data/useCampaignCardLiveData';
import { useCampaignCardActive } from './useCampaignCardActive';

const ICON_PATHS = {
  period: '/campaigns/icons/IconsPeriod',
  prizes: '/campaigns/icons/IconsTotalLeftPrizes',
  clock: '/campaigns/icons/IconsClock',
  position: '/campaigns/icons/Position',
  score: '/campaigns/icons/Score',
  claimedWin: '/campaigns/icons/claimedWin',
  progress: '/campaigns/icons/Progress',
} as const;

interface UseCampaignInfoItemsProps {
  staticTiming: {
    startDate: string;
    endDate: string;
  };
  isActiveSlot: boolean;
  campaign: CampaignSource |  undefined;
}

export const useCampaignInfoItems = ({ staticTiming, isActiveSlot, campaign }: UseCampaignInfoItemsProps) => {
  const { t } = useTranslation();
  const isSingleView = useViewMode() === SINGLE_VIEW_MODE;
  const currency = useCurrency();
  const campaignId = campaign?.id;
  const campaignType = useCampaignTypeById(campaignId);

  // Whether this card should run its live sub-fetches. Visibility is observed on
  // the card root (see CampaignCard); this just reads it.
  const isCardActive = useCampaignCardActive(campaignId, campaignType);

  // Variant-specific live values (prizes / leaderboard / rebate) are resolved by type in
  // a single dispatcher — this generic hook stays type-agnostic.
  const { prizesLeft, playerPosition, playerScore, claimedWinDisplay, progressDisplay, showCurrency } = useCampaignCardLiveData(campaign, isCardActive);

  const infoItemsConfig = useMemo((): Record<CampaignInfoType, CampaignInfoItem> => ({
    [CAMPAIGN_INFO_TYPES.PERIOD]: {
      iconSrc: ICON_PATHS.period,
      type: CAMPAIGN_INFO_TYPES.PERIOD,
      label: isSingleView ? t('campaigns.period', 'Period') : '',
      value: staticTiming.startDate && staticTiming.endDate
        ? (staticTiming.startDate === staticTiming.endDate ? staticTiming.endDate : `${staticTiming.startDate} - ${staticTiming.endDate}`)
        : 'N/A',
    },
    [CAMPAIGN_INFO_TYPES.TIMER]: {
      iconSrc: ICON_PATHS.clock,
      type: CAMPAIGN_INFO_TYPES.TIMER,
      // Label and value are handled by CountdownTimer component — these are placeholders
      label: '',
      value: '',
    },
    [CAMPAIGN_INFO_TYPES.PRIZES]: {
      iconSrc: ICON_PATHS.prizes,
      type: CAMPAIGN_INFO_TYPES.PRIZES,
      label: isActiveSlot
        ? t('campaigns.prizes_left', 'Prizes')
        : t('campaigns.total_prizes', 'Prizes'),
      value: formatNumberWithSpaces(prizesLeft),
    },
    [CAMPAIGN_INFO_TYPES.POSITION]: {
      iconSrc: ICON_PATHS.position,
      type: CAMPAIGN_INFO_TYPES.POSITION,
      label: t('campaigns.position', 'Position'),
      value: playerPosition != null && playerPosition >= 0 ? String(playerPosition) : '-',
    },
    [CAMPAIGN_INFO_TYPES.SCORE]: {
      iconSrc: ICON_PATHS.score,
      type: CAMPAIGN_INFO_TYPES.SCORE,
      label: t('campaigns.score', 'Score'),
      value: playerScore != null ? formatNumberWithSpaces(playerScore) : '0',
    },
    [CAMPAIGN_INFO_TYPES.CLAIMED_WIN]: {
      iconSrc: ICON_PATHS.claimedWin,
      type: CAMPAIGN_INFO_TYPES.CLAIMED_WIN,
      label: t('campaigns.claimedWin', 'Claimed Win'),
      value: claimedWinDisplay,
      suffix: showCurrency ? campaign?.baseCurrency ?? currency : undefined,
    },
    [CAMPAIGN_INFO_TYPES.PROGRESS]: {
      iconSrc: ICON_PATHS.progress,
      type: CAMPAIGN_INFO_TYPES.PROGRESS,
      label: t('campaigns.progress', 'Progress'),
      value: progressDisplay,
    },
  }), [staticTiming, isActiveSlot, campaign, t, prizesLeft, isSingleView, currency, playerPosition, playerScore, claimedWinDisplay, progressDisplay, showCurrency]);

  const getSelectedInfoItems = useMemo(() => {
    return (infoTypes: CampaignInfoType[]) => infoTypes.map((type) => infoItemsConfig[type]).filter(Boolean);
  }, [infoItemsConfig]);

  return {
    infoItemsConfig,
    getSelectedInfoItems,
  };
};
